/** @file Sparse evaluation result of one animation at one time, with a keyed-channel mask (frozen contract). */
import type { SkeletonData } from '../skeletonData';
import { POSE_LAYOUT } from './pose';

/**
 * Channel layout of `values`/`mask`: bones × LOCAL_STRIDE, then slots × SLOT_STRIDE (per-channel colour
 * floats), then constraints × IK_STRIDE (offsets from channelOffsets). Discrete channels live in their own
 * arrays. Everything is allocated once by createSample and never replaced (zero allocation per frame).
 */
export interface Sample {
  values: Float64Array;
  mask: Uint8Array;
  /** InheritMode code per bone, -1 = not keyed. */
  inherit: Int8Array;
  /** Placeholder id per slot (index into `SkeletonData.placeholders`), -2 = not keyed, -1 = keyed to none. */
  attachments: Int32Array;
  /** Full permutation of slot indices; meaningful only when `drawOrderKeyed`. */
  drawOrder: Int32Array;
  drawOrderKeyed: boolean;
  /** Deform offsets per mesh attachment (compiled form), allocated by createSample; read only where `deformKeyed[i] === 1`. */
  deform: Float64Array[];
  deformKeyed: Uint8Array;
  /** Key indices into the animation's event timeline (`AnimationData.eventTimeline`) fired in the sampled interval. */
  events: Int32Array;
  eventCount: number;
}

export interface ChannelOffsets {
  boneStart: 0;
  slotStart: number;
  ikStart: number;
  end: number;
}

/** Computes where each entity's channels start inside Sample.values for this skeleton. */
export function channelOffsets(data: SkeletonData): ChannelOffsets {
  const slotStart = data.bones.length * POSE_LAYOUT.LOCAL_STRIDE;
  const ikStart = slotStart + data.slots.length * POSE_LAYOUT.SLOT_STRIDE;
  return { boneStart: 0, slotStart, ikStart, end: ikStart + data.ik.length * POSE_LAYOUT.IK_STRIDE };
}
